import { FC, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import type { ClaimFilterView } from "../../../../types/claims";
import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import useNotifier from "../../../../hooks/useNotifier";

export interface ClaimFilterViewDeleteDialogProps {
  open: boolean;
  view: ClaimFilterView | null;
  onClose: () => void;
}

export const ClaimFilterViewDeleteDialog: FC<
  ClaimFilterViewDeleteDialogProps
> = ({ open, view, onClose }) => {
  const theme = useTheme();
  const notifier = useNotifier()
  const claimsStore = useClaimsStore();

  const [isDeleting, setIsDeleting] = useState(false);

  const onConfirmDelete = async () => {
    setIsDeleting(true);

    const isSuccessful = await claimsStore.deleteClaimFilterView(view?.id || '');

    if (isSuccessful) {
      onClose();
      notifier.showSuccess("View deleted successfully");
    } else {
      notifier.showError("Could not delete this view. Try again.");
    }

    setIsDeleting(false);
  };

  return (
    <Dialog open={open} onClose={() => !isDeleting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        Delete saved view?
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" sx={{ paddingTop: 2,}}>
          This will remove{" "}
          <Typography component="span" fontWeight={600}>
            {view?.name}
          </Typography>{" "}
          from your saved views.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirmDelete}
          disabled={isDeleting}
          loading={isDeleting}
          sx={{ textTransform: "none" }}
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
